import React, { FC, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';

interface UploadedFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  error: string | null;
}

interface UploadStatusDialogProps {
  open: boolean;
  onClose: () => void;
  uploadQueue: UploadedFile[];
  onClearFile: (id: string) => void;
  onClearAll: () => void;
}

const UploadStatusDialog: FC<UploadStatusDialogProps> = ({
  open,
  onClose,
  uploadQueue,
  onClearFile,
  onClearAll,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  // Display only the 10 most recent uploads, or all if less than 10
  const displayQueue = uploadQueue.slice(-10);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent"
      sx={{
        zIndex: 1500,
        '.MuiDrawer-paper': {
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: { xs: '100%', sm: 400 },
          height: isCollapsed ? 60 : { xs: '50%', sm: 400 },
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          bgcolor: '#111',
          color: '#FFF',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: 'none'
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          bgcolor: 'transparent'
        }}
        onClick={handleToggleCollapse}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFF' }}>Uploads ({uploadQueue.length})</Typography>
        <Box>
          {uploadQueue.length > 0 && (
            <Button onClick={onClearAll} size="small" color="error" sx={{ mr: 1 }}>
              Clear All
            </Button>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
          <IconButton onClick={handleToggleCollapse} size="small" sx={{ color: '#666' }}>
            {isCollapsed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Paper>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)' }} />
      {!isCollapsed && (
        <List sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
          {displayQueue.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No active uploads."
                primaryTypographyProps={{ sx: { color: '#666', textAlign: 'center', py: 4 } }}
              />
            </ListItem>
          ) : (
            displayQueue.map((file) => (
              <ListItem
                key={file.id}
                secondaryAction={
                  <IconButton edge="end" aria-label="delete" onClick={() => onClearFile(file.id)} sx={{ color: '#EF4444' }}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={file.file.name}
                  primaryTypographyProps={{ sx: { color: '#FFF', fontWeight: 600 } }}
                  secondary={
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={file.progress}
                        sx={{
                          my: 1,
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '4px',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: file.status === 'completed' ? '#10B981' : file.status === 'failed' ? '#EF4444' : '#3B82F6'
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                        {file.status === 'uploading'
                          ? `Uploading: ${file.progress}%`
                          : file.status === 'completed'
                          ? <Box component="span" sx={{ color: '#10B981' }}>Completed</Box>
                          : <Box component="span" sx={{ color: '#EF4444' }}>Failed: {file.error}</Box>}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      )}
    </Drawer>
  );
};

export default UploadStatusDialog;
